import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { Checkbox } from 'blaze-ui/Checkbox'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { BrandKitSource } from 'types'
import {
  getDisplayName,
  getSourceAltName,
  getSourceFavicon,
  getSourceIcon,
} from 'screens/BrandKits/components/VMSourceMaterials/VMSourceMaterialsV2/utils'
import Styles from './SourceMaterialsSection.module.scss'

interface SourceMaterialsSectionProps {
  isLoading: boolean
  brandKitSources: BrandKitSource[]
  existingSourceIds: Set<number>
  selectedSourceIds: Set<number>
  onToggle: (id: number) => void
}

export const SourceMaterialsSection = ({
  isLoading,
  brandKitSources,
  existingSourceIds,
  selectedSourceIds,
  onToggle,
}: SourceMaterialsSectionProps) => {
  if (isLoading) {
    return (
      <div className={cx(Styles.list, Styles.loading)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <LoadingShimmerer key={i} className={Styles.rowShimmer} borderRadius={8} />
        ))}
      </div>
    )
  }

  if (brandKitSources.length === 0) {
    return (
      <div className={Styles.empty}>
        <Text variant="secondary" color="var(--dark-40)">
          No source materials in your Brand Kit
        </Text>
      </div>
    )
  }

  return (
    <div className={Styles.list}>
      {brandKitSources.map((brandKitSource: BrandKitSource) => {
        const id = brandKitSource.source.id as number
        const alreadyAdded = existingSourceIds.has(id)
        const isSelected = selectedSourceIds.has(id)
        const favicon = getSourceFavicon(brandKitSource)
        const { icon: SourceIconComp, color } = getSourceIcon(brandKitSource)
        const altName = getSourceAltName(brandKitSource)
        const name = getDisplayName(brandKitSource)

        return (
          <Checkbox.Label
            key={id}
            isSelected={isSelected || alreadyAdded}
            isDisabled={alreadyAdded}
            onChange={() => !alreadyAdded && onToggle(id)}
            aria-label={name}
            className={cx(Styles.row, { [Styles.disabled]: alreadyAdded })}
          >
            <Checkbox.Box size={20} />
            <div className={Styles.rowIcon}>
              {favicon ? (
                <img src={favicon} alt="Favicon" className={Styles.favicon} />
              ) : (
                <SourceIconComp size={32} color={color} />
              )}
            </div>
            <div className={Styles.rowInfo}>
              <Text variant="secondary" lineClamp={1}>
                {name}
              </Text>
              {altName && (
                <Text variant="secondary" lineClamp={1} color="var(--dark-40)">
                  {altName}
                </Text>
              )}
            </div>
          </Checkbox.Label>
        )
      })}
    </div>
  )
}
